import { lazy } from "common/load/componentUtils";

import { useDrawer, useDialog, CloseProp } from "client/common/hooks/stackables";

import type { CreateAddressDrawerProps } from "../components/stackables/CreateAddressDrawer";
import type { AddressUpdateDrawerProps } from "../components/stackables/AddressUpdateDrawer";
import type { NewAddressConfirmationDrawerProps } from "../components/stackables/NewAddressConfirmationDrawer";
import type { ExistingAddressConfirmationDialogProps } from "../components/stackables/ExistingAddressConfirmationDialog";
import type { AccountUpdateAddressDrawerProps } from "../components/stackables/AccountUpdateAddressDrawer";
import type { AccountUpdatePaymentDrawerProps } from "../components/stackables/AccountUpdatePaymentDrawer";
import type { AccountUpdatePetDrawerProps } from "../components/stackables/AccountUpdatePetDrawer";
import type { VerifyPhoneDrawerProps } from "../components/stackables/VerifyPhoneDrawer";
import type { RemovePaymentDrawerProps } from "../components/stackables/RemovePaymentDrawer";
import type { RemoveAddressDrawerProps } from "../components/stackables/RemoveAddressDrawer";
import type { AccountUpdateDrawerProps } from "../components/stackables/AccountUpdateDrawer";
import type { AccountUpdatePhoneDrawerProps } from "../components/stackables/AccountUpdatePhoneDrawer";

const CreateAddressDrawer = lazy(() => import("../components/stackables/CreateAddressDrawer"));
const AddressUpdateDrawer = lazy(() => import("../components/stackables/AddressUpdateDrawer"));
const NewAddressConfirmationDrawer = lazy(
  () => import("../components/stackables/NewAddressConfirmationDrawer"),
);
const ExistingAddressConfirmationDialog = lazy(
  () => import("../components/stackables/ExistingAddressConfirmationDialog"),
);
const AccountUpdatePhoneDrawer = lazy(
  () => import("../components/stackables/AccountUpdatePhoneDrawer"),
);
const AccountUpdateDrawer = lazy(() => import("../components/stackables/AccountUpdateDrawer"));
const AccountUpdatePasswordDrawer = lazy(
  () => import("../components/stackables/AccountUpdatePasswordDrawer"),
);
const AccountUpdateNameDrawer = lazy(
  () => import("../components/stackables/AccountUpdateNameDrawer"),
);
const AccountUpdateEmailDrawer = lazy(
  () => import("../components/stackables/AccountUpdateEmailDrawer"),
);
const AccountUpdateCommsEmailDrawer = lazy(
  () => import("../components/stackables/AccountUpdateCommsEmailDrawer"),
);
const AccountUpdateCommunicationPreferencesDrawer = lazy(
  () => import("../components/stackables/AccountUpdateCommunicationPreferencesDrawer"),
);
const AccountControlledSubstancesFieldsUpdateDrawer = lazy(
  () => import("../components/stackables/AccountControlledSubstancesFieldsUpdateDrawer"),
);
const AccountUpdateAddressDrawer = lazy(
  () => import("../components/stackables/AccountUpdateAddressDrawer"),
);
const AccountUpdatePaymentDrawer = lazy(
  () => import("../components/stackables/AccountUpdatePaymentDrawer"),
);
const AccountUpdatePetDrawer = lazy(
  () => import("../components/stackables/AccountUpdatePetDrawer"),
);
const AccountCreatePaymentDrawer = lazy(
  () => import("../components/stackables/AccountCreatePaymentDrawer"),
);
const AccountCreatePetDrawer = lazy(
  () => import("../components/stackables/AccountCreatePetDrawer"),
);
const VerifyPhoneDrawer = lazy(() => import("../components/stackables/VerifyPhoneDrawer"));
const RemovePaymentDrawer = lazy(() => import("../components/stackables/RemovePaymentDrawer"));
const RemoveAddressDrawer = lazy(() => import("../components/stackables/RemoveAddressDrawer"));
const ControlledSubstancesInfoDrawer = lazy(
  () => import("../components/stackables/ControlledSubstancesInfoDrawer"),
);

export const useCreateAddressDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CreateAddressDrawerProps & CloseProp) => open(CreateAddressDrawer, props),
  };
};

export const useAddressUpdateDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: AddressUpdateDrawerProps & CloseProp) => open(AddressUpdateDrawer, props),
  };
};

export const useNewAddressConfirmationDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: NewAddressConfirmationDrawerProps & CloseProp) =>
      open(NewAddressConfirmationDrawer, props),
  };
};

export const useExistingAddressConfirmationDialog = () => {
  const { open } = useDialog();
  return {
    open: (props: ExistingAddressConfirmationDialogProps & CloseProp) =>
      open(ExistingAddressConfirmationDialog, props),
  };
};

export const useAccountUpdatePhoneDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: AccountUpdatePhoneDrawerProps & CloseProp) =>
      open(AccountUpdatePhoneDrawer, props),
  };
};

export const useAccountUpdateDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: AccountUpdateDrawerProps & CloseProp) => open(AccountUpdateDrawer, props),
  };
};

export const useAccountUpdatePasswordDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CloseProp) => open(AccountUpdatePasswordDrawer, props),
  };
};

export const useAccountUpdateNameDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CloseProp) => open(AccountUpdateNameDrawer, props),
  };
};

export const useAccountUpdateEmailDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CloseProp) => open(AccountUpdateEmailDrawer, props),
  };
};

export const useAccountUpdateCommsEmailDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CloseProp) => open(AccountUpdateCommsEmailDrawer, props),
  };
};

export const useAccountUpdateCommunicationPreferencesDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CloseProp) => open(AccountUpdateCommunicationPreferencesDrawer, props),
  };
};

export const useAccountControlledSubstancesFieldsUpdateDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CloseProp) => open(AccountControlledSubstancesFieldsUpdateDrawer, props),
  };
};

export const useAccountUpdateAddressDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: AccountUpdateAddressDrawerProps & CloseProp) =>
      open(AccountUpdateAddressDrawer, props),
  };
};

export const useAccountUpdatePaymentDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: AccountUpdatePaymentDrawerProps & CloseProp) =>
      open(AccountUpdatePaymentDrawer, props),
  };
};

export const useAccountUpdatePetDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: AccountUpdatePetDrawerProps & CloseProp) => open(AccountUpdatePetDrawer, props),
  };
};

export const useAccountCreatePaymentDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CloseProp) => open(AccountCreatePaymentDrawer, props),
  };
};

export const useAccountCreatePetDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: CloseProp) => open(AccountCreatePetDrawer, props),
  };
};

export const useVerifyPhoneDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: VerifyPhoneDrawerProps & CloseProp) => open(VerifyPhoneDrawer, props),
  };
};

export const useRemovePaymentDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: RemovePaymentDrawerProps & CloseProp) => open(RemovePaymentDrawer, props),
  };
};

export const useRemoveAddressDrawer = () => {
  const { open } = useDrawer();
  return {
    open: (props: RemoveAddressDrawerProps & CloseProp) => open(RemoveAddressDrawer, props),
  };
};

export const useControlledSubstancesInfoDrawer = () => {
  const { open } = useDrawer();
  return {
    open: () => open(ControlledSubstancesInfoDrawer),
  };
};
